import { normalizeWorkspace } from "../domain.js";

const STORAGE_KEY = "rhythm-schedule-v2";

export class LocalRepository {
  constructor(emptyFactory) {
    this.emptyFactory = emptyFactory;
    this.mode = "local";
  }

  async loadWorkspace() {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return normalizeWorkspace(this.emptyFactory());
    try {
      const workspace = normalizeWorkspace(JSON.parse(raw));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(workspace));
      return workspace;
    } catch (error) {
      console.warn("本地数据无法读取，已打开空白工作区。", error);
      return normalizeWorkspace(this.emptyFactory());
    }
  }

  async saveWorkspace(workspace) {
    const normalized = normalizeWorkspace(workspace);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    return normalized;
  }
}

export class SupabaseRepository {
  constructor({ client, userId }) {
    this.client = client;
    this.userId = userId;
    this.mode = "supabase";
  }

  async rows(query) {
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  async loadWorkspace() {
    if (!this.userId) throw new Error("Supabase session is required");
    const [projects, tasks, events, recurrences] = await Promise.all([
      this.rows(this.client.from("projects").select("*").order("sort_order", { ascending: true })),
      this.rows(this.client.from("tasks").select("*").order("created_at", { ascending: false })),
      this.rows(this.client.from("schedule_events").select("*").order("starts_at", { ascending: true })),
      this.rows(this.client.from("recurrence_series").select("*").order("created_at", { ascending: true })),
    ]);
    return normalizeWorkspace({
      projects: projects.map(projectFromRow),
      tasks: tasks.map(taskFromRow),
      events: events.map(eventFromRow),
      recurrences: recurrences.map(seriesFromRow),
    });
  }

  async saveWorkspace(workspace) {
    if (!this.userId) throw new Error("Supabase session is required");
    const normalized = normalizeWorkspace(workspace);
    const tables = [
      ["projects", normalized.projects.map((item) => projectToRow(item, this.userId))],
      ["recurrence_series", normalized.recurrences.map((item) => seriesToRow(item, this.userId))],
      ["tasks", normalized.tasks.map((item) => taskToRow(item, this.userId))],
      ["schedule_events", normalized.events.map((item) => eventToRow(item, this.userId))],
    ];

    for (const [table, rows] of tables) await this.upsert(table, rows);
    for (const [table, rows] of [...tables].reverse()) await this.deleteMissing(table, rows.map((row) => row.id));
    return normalized;
  }

  async upsert(table, rows) {
    if (!rows.length) return [];
    return this.rows(this.client.from(table).upsert(rows).select("id"));
  }

  async deleteMissing(table, retainedIds) {
    const rows = await this.rows(this.client.from(table).select("id"));
    const retained = new Set(retainedIds);
    const missing = rows.map((row) => row.id).filter((id) => !retained.has(id));
    if (!missing.length) return;
    const { error } = await this.client.from(table).delete().in("id", missing);
    if (error) throw error;
  }
}

export function createRepository(emptyFactory, { client, userId } = {}) {
  if (client && userId) return new SupabaseRepository({ client, userId });
  return new LocalRepository(emptyFactory);
}

function projectFromRow(row) {
  return { id: row.id, name: row.name, color: row.color, sortOrder: row.sort_order, isArchived: row.is_archived };
}

function taskFromRow(row) {
  return {
    id: row.id,
    projectId: row.project_id,
    recurrenceSeriesId: row.recurrence_series_id,
    generatedFromTaskId: row.generated_from_task_id,
    title: row.title,
    details: row.details,
    estimatedMinutes: row.estimated_minutes,
    plannedDate: row.planned_date,
    color: row.color,
    sourceUrl: row.source_url,
    sourceTitle: row.source_title,
    sourceDomain: row.source_domain,
    completedAt: row.completed_at,
    skippedAt: row.skipped_at,
    createdAt: row.created_at,
    type: row.type,
    recurrenceRule: row.recurrence_rule,
    recurrenceAnchorDate: row.recurrence_anchor_date,
  };
}

function eventFromRow(row) {
  return { id: row.id, taskId: row.task_id, startsAt: row.starts_at, endsAt: row.ends_at, completedAt: row.completed_at };
}

function seriesFromRow(row) {
  return {
    id: row.id,
    projectId: row.project_id,
    title: row.title,
    details: row.details,
    estimatedMinutes: row.estimated_minutes,
    color: row.color,
    sourceUrl: row.source_url,
    sourceTitle: row.source_title,
    sourceDomain: row.source_domain,
    startDate: row.start_date,
    endDate: row.end_date,
    rule: row.rule,
    isActive: row.is_active,
    createdAt: row.created_at,
  };
}

function projectToRow(item, userId) {
  return { id: item.id, user_id: userId, name: item.name, color: item.color, sort_order: item.sortOrder || 0, is_archived: Boolean(item.isArchived) };
}

function taskToRow(item, userId) {
  return {
    id: item.id,
    user_id: userId,
    project_id: item.projectId,
    recurrence_series_id: item.recurrenceSeriesId,
    generated_from_task_id: item.generatedFromTaskId,
    type: item.recurrenceSeriesId ? "routine" : item.sourceUrl ? "link" : "one_time",
    title: item.title,
    details: item.details || "",
    estimated_minutes: item.estimatedMinutes,
    planned_date: item.plannedDate,
    color: item.color,
    source_url: item.sourceUrl,
    source_title: item.sourceTitle,
    source_domain: item.sourceDomain,
    unread: false,
    recurrence_rule: null,
    recurrence_anchor_date: null,
    completed_at: item.completedAt,
    archived_at: item.completedAt,
    skipped_at: item.skippedAt,
  };
}

function eventToRow(item, userId) {
  return { id: item.id, user_id: userId, task_id: item.taskId, starts_at: item.startsAt, ends_at: item.endsAt, completed_at: item.completedAt };
}

function seriesToRow(item, userId) {
  return {
    id: item.id,
    user_id: userId,
    project_id: item.projectId,
    title: item.title,
    details: item.details || "",
    estimated_minutes: item.estimatedMinutes,
    color: item.color,
    source_url: item.sourceUrl,
    source_title: item.sourceTitle,
    source_domain: item.sourceDomain,
    start_date: item.startDate,
    end_date: item.endDate,
    rule: item.rule,
    is_active: item.isActive !== false,
  };
}
